import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { priceHotelByCode } from '../api/Hotels';
import { InsuranceDetail, VisaDetail } from '../types/PackageBuilder';
import { ItineraryItem, Transfer, TripInfo } from '../types/Quotation';
import { getDatePlusDays } from '../utils/date';
import { QuotationRoomOccupancy, toHotelSearchRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';
import { resolveQuotationHotelCode } from '../components/QuotationBuilder/quotationHotelCode';

type Addon = {
  name: string;
  price: number;
  currency: string;
  description: string;
  quoteBasePrice?: number;
};

type UseQuotationRepricingArgs = {
  builderMode: 'package' | 'quotation';
  tripInfo: TripInfo;
  itinerary: ItineraryItem[];
  setItinerary: React.Dispatch<React.SetStateAction<ItineraryItem[]>>;
  visaDetails: VisaDetail[];
  setVisaDetails: React.Dispatch<React.SetStateAction<VisaDetail[]>>;
  insuranceDetails: InsuranceDetail[];
  setInsuranceDetails: React.Dispatch<React.SetStateAction<InsuranceDetail[]>>;
  addons: Addon[];
  setAddons: React.Dispatch<React.SetStateAction<Addon[]>>;
  setPriceOverrides: React.Dispatch<React.SetStateAction<{
    flights: number | null;
    hotels: number | null;
    transfers: number | null;
    visa: number | null;
    insurance: number | null;
    activities: number | null;
    addons: number | null;
  }>>;
  quotationHotelRooms?: QuotationRoomOccupancy[];
};

const EMPTY_OVERRIDES = {
  flights: null,
  hotels: null,
  transfers: null,
  visa: null,
  insurance: null,
  activities: null,
  addons: null,
};

const buildTravellersKey = (travellers: TripInfo['travellers']) => [
  Number(travellers?.adults || 0),
  Number(travellers?.children || 0),
  Number(travellers?.infants || 0),
].join(':');

export const getQuotationPaxMultiplier = (travellers: TripInfo['travellers']) => {
  const totalPax = Number(travellers?.adults || 0)
    + Number(travellers?.children || 0)
    + Number(travellers?.infants || 0);
  return Math.max(1, Math.ceil(Math.max(1, totalPax) / 2));
};

const scaleTransfer = (transfer: Transfer, multiplier: number): Transfer => {
  const currentPrice = Number(transfer.price || 0);
  if (!Number.isFinite(currentPrice) || currentPrice <= 0) return transfer;
  const quoteBasePrice = Number(transfer.quoteBasePrice ?? currentPrice);
  if (!Number.isFinite(quoteBasePrice) || quoteBasePrice <= 0) return transfer;
  return {
    ...transfer,
    quoteBasePrice,
    price: quoteBasePrice * multiplier,
  };
};

const scaleAddon = <T extends { price: number; quoteBasePrice?: number }>(item: T, multiplier: number): T => {
  const currentPrice = Number(item.price || 0);
  if (!Number.isFinite(currentPrice) || currentPrice <= 0) return item;
  const quoteBasePrice = Number(item.quoteBasePrice ?? currentPrice);
  if (!Number.isFinite(quoteBasePrice) || quoteBasePrice <= 0) return item;
  return {
    ...item,
    quoteBasePrice,
    price: quoteBasePrice * multiplier,
  };
};

const computePlaceDateRanges = (startDate: string, places: TripInfo['places']) => {
  let offsetDays = 0;
  return places.map((place, idx) => {
    const checkIn = getDatePlusDays(startDate, offsetDays);
    const nights = Math.max(1, Number(place.nights || 1));
    const checkOut = getDatePlusDays(checkIn, nights);
    const start = offsetDays;
    const end = offsetDays + nights;
    offsetDays = end;
    return { idx, start, end, checkIn, checkOut };
  });
};

const findPlaceIndexForDay = (
  dayIndex: number,
  ranges: Array<{ idx: number; start: number; end: number }>
) => {
  const hit = ranges.find((range) => dayIndex >= range.start && dayIndex < range.end);
  if (hit) return hit.idx;
  return Math.max(0, ranges.length - 1);
};

const alignHotelsToPlaceRanges = (
  itinerary: ItineraryItem[],
  ranges: Array<{ idx: number; start: number; end: number; checkIn: string; checkOut: string }>
) => {
  let changed = false;

  const next = itinerary.map((day, dayIndex) => {
    const hotels = day.hotels || [];
    if (!hotels.length) return day;

    const placeIdx = findPlaceIndexForDay(dayIndex, ranges);
    const range = ranges[placeIdx];
    if (!range) return day;

    let dayChanged = false;
    const nextHotels = hotels.map((hotel) => {
      const nextCheckInDate = range.checkIn || hotel.checkInDate;
      const nextCheckOutDate = range.checkOut || hotel.checkOutDate;
      if (hotel.checkInDate === nextCheckInDate && hotel.checkOutDate === nextCheckOutDate) {
        return hotel;
      }
      changed = true;
      dayChanged = true;
      return {
        ...hotel,
        checkInDate: nextCheckInDate,
        checkOutDate: nextCheckOutDate,
      };
    });

    return dayChanged ? { ...day, hotels: nextHotels } : day;
  });

  return changed ? next : itinerary;
};

export const useQuotationRepricing = ({
  builderMode,
  tripInfo,
  itinerary,
  setItinerary,
  visaDetails,
  setVisaDetails,
  insuranceDetails,
  setInsuranceDetails,
  addons,
  setAddons,
  setPriceOverrides,
  quotationHotelRooms,
}: UseQuotationRepricingArgs) => {
  const [flightReselectRequired, setFlightReselectRequired] = useState(false);
  const [flightReselectTrigger, setFlightReselectTrigger] = useState(0);
  const [hotelRepriceWarnings, setHotelRepriceWarnings] = useState<string[]>([]);
  const [isHotelRepricing, setIsHotelRepricing] = useState(false);
  const initializedRef = useRef(false);
  const lastInputsRef = useRef<{ startDate: string; travellersKey: string; hotelRoomsKey: string }>({
    startDate: '',
    travellersKey: '',
    hotelRoomsKey: '',
  });
  const requestIdRef = useRef(0);

  const travellersKey = useMemo(() => buildTravellersKey(tripInfo.travellers), [tripInfo.travellers]);
  const hotelRoomsKey = useMemo(() => JSON.stringify(Array.isArray(quotationHotelRooms) ? quotationHotelRooms : []), [quotationHotelRooms]);

  const markFlightsReselected = useCallback(() => {
    setFlightReselectRequired(false);
  }, []);

  useEffect(() => {
    if (builderMode !== 'quotation') return;
    if (!tripInfo.startDate) return;

    const placeRanges = computePlaceDateRanges(tripInfo.startDate, tripInfo.places);
    if (placeRanges.length === 0) return;

    setItinerary((prev) => alignHotelsToPlaceRanges(prev, placeRanges));
  }, [builderMode, itinerary, setItinerary, tripInfo.places, tripInfo.startDate]);

  useEffect(() => {
    if (builderMode !== 'quotation') return;

    const nextInputs = {
      startDate: (tripInfo.startDate || '').trim(),
      travellersKey,
      hotelRoomsKey,
    };

    if (!initializedRef.current) {
      initializedRef.current = true;
      lastInputsRef.current = nextInputs;
      return;
    }

    if (
      lastInputsRef.current.startDate === nextInputs.startDate
      && lastInputsRef.current.travellersKey === nextInputs.travellersKey
      && lastInputsRef.current.hotelRoomsKey === nextInputs.hotelRoomsKey
    ) {
      return;
    }

    lastInputsRef.current = nextInputs;

    if (!nextInputs.startDate || !tripInfo.travellers) return;

    const hasAnySelectedItems = itinerary.some((day) =>
      (day.flights || []).length > 0
      || (day.hotels || []).length > 0
      || (day.transfers || []).length > 0
      || (day.activities || []).length > 0
    ) || visaDetails.length > 0 || insuranceDetails.length > 0 || addons.length > 0;

    if (!hasAnySelectedItems) return;

    const multiplier = getQuotationPaxMultiplier(tripInfo.travellers);
    const hasFlights = itinerary.some((day) => (day.flights || []).length > 0);
    const hasHotels = itinerary.some((day) => (day.hotels || []).length > 0);
    const placeRanges = computePlaceDateRanges(tripInfo.startDate, tripInfo.places);
    const requestId = ++requestIdRef.current;

    setPriceOverrides(EMPTY_OVERRIDES);
    setHotelRepriceWarnings([]);

    setItinerary((prev) => prev.map((day) => ({
      ...day,
      transfers: (day.transfers || []).map((transfer) => scaleTransfer(transfer, multiplier)),
      activities: day.activities || [],
    })));
    setVisaDetails((prev) => prev.map((item) => scaleAddon(item, multiplier)));
    setInsuranceDetails((prev) => prev.map((item) => scaleAddon(item, multiplier)));
    setAddons((prev) => prev.map((item) => scaleAddon(item, multiplier)));

    if (hasFlights) {
      setFlightReselectRequired(true);
      setFlightReselectTrigger((prev) => prev + 1);
    } else {
      setFlightReselectRequired(false);
    }

    if (!hasHotels || placeRanges.length === 0) {
      setIsHotelRepricing(false);
      return;
    }

    setIsHotelRepricing(true);

    void (async () => {
      const warnings: string[] = [];
      const repricedHotelsByDay = await Promise.all(itinerary.map(async (day, dayIndex) => {
        const placeIdx = findPlaceIndexForDay(dayIndex, placeRanges);
        const range = placeRanges[placeIdx];
        const rooms = Array.isArray(quotationHotelRooms) && quotationHotelRooms.length > 0
          ? toHotelSearchRooms(quotationHotelRooms)
          : [{
            Adults: Number(tripInfo.travellers?.adults || 0),
            Children: Number(tripInfo.travellers?.children || 0),
            ChildrenAges: [],
          }];
        return Promise.all((day.hotels || []).map(async (hotel) => {
          const hotelCode = resolveQuotationHotelCode(hotel);
          const baseHotel = {
            ...hotel,
            checkInDate: range?.checkIn || hotel.checkInDate,
            checkOutDate: range?.checkOut || hotel.checkOutDate,
          };

          if (!hotelCode) {
            warnings.push(`Could not reprice hotel ${hotel.name || 'Unknown hotel'} because no hotel code/reference was found.`);
            return baseHotel;
          }

          try {
            const pricedHotel = await priceHotelByCode({
              hotelCode: String(hotelCode),
              checkIn: range?.checkIn || hotel.checkInDate || '',
              checkOut: range?.checkOut || hotel.checkOutDate || '',
              rooms,
            });

            const effectivePrice = Number(
              Number.isFinite(Number(pricedHotel.displayPrice))
                ? pricedHotel.displayPrice
                : pricedHotel.price
            );

            if (!Number.isFinite(effectivePrice)) {
              warnings.push(`Could not fetch a new price for hotel ${hotel.name || hotelCode}. Keeping the previous hotel price.`);
              return baseHotel;
            }

            return {
              ...baseHotel,
              price: effectivePrice,
              currency: pricedHotel.currency || baseHotel.currency,
              display_price: effectivePrice,
              display_currency: pricedHotel.displayCurrency || pricedHotel.currency || baseHotel.display_currency,
            };
          } catch {
            warnings.push(`Failed to reprice hotel ${hotel.name || hotelCode}. Keeping the previous hotel price.`);
            return baseHotel;
          }
        }));
      }));

      if (requestIdRef.current !== requestId) return;

      setItinerary((prev) => prev.map((day, dayIndex) => ({
        ...day,
        hotels: repricedHotelsByDay[dayIndex] || day.hotels,
      })));
      setHotelRepriceWarnings(warnings);
      setIsHotelRepricing(false);
    })();
  }, [
    addons,
    builderMode,
    insuranceDetails,
    itinerary,
    setAddons,
    setInsuranceDetails,
    setItinerary,
    setPriceOverrides,
    setVisaDetails,
    quotationHotelRooms,
    travellersKey,
    hotelRoomsKey,
    tripInfo,
    visaDetails,
  ]);

  return {
    flightReselectRequired,
    flightReselectTrigger,
    hotelRepriceWarnings,
    isHotelRepricing,
    markFlightsReselected,
  };
};
